<template>
  <div class="tab-content content-scroll-sm">
    <!-- No data at all -->
    <div v-if="!hasAnyData" class="status-msg">
      <span>
        <svgicon name="empty-timeline" width="249" height="176" :original="true" />
        <p>No data found</p>
      </span>
    </div>

    <Row v-else class="timeline-row">
      <!-- Timeline column -->
      <Col :xs="24" :sm="24" :md="24" :lg="hasPingHistory ? 13 : 24" :xl="hasPingHistory ? 13 : 24">
        <!-- Courier timeline events -->
        <template v-if="courierTimeline && courierTimeline.length">
          <Timeline class="timeline-default mt-10 timeline-with-action">
            <TimelineItem
              v-for="(event, idx) in courierTimeline"
              :key="'c-' + idx + event.time"
              placement="top"
              class="dot-purple mb-5">
              <div class="el-timeline-item__timestamp is-top">
                <span class="date">{{ formatDate(event.time) }}</span>
                <span>{{ formatTime(event.time) }}</span>
              </div>
              <h4>{{ event.type }}</h4>
              <p v-if="event.name">{{ event.name }}</p>
              <p v-if="event.reason && event.reason !== ''">Reason: {{ event.reason }}</p>
              <p v-if="event.pincode">{{ event.pincode }}</p>
              <p v-if="event.remarks">{{ event.remarks }}</p>
            </TimelineItem>
          </Timeline>
        </template>

        <!-- Milestone timeline events -->
        <template v-if="computedMilestones.length">
          <Timelineblock :timelineEvents="computedMilestones" />
        </template>
      </Col>

      <!-- Ping history column -->
      <Col v-if="hasPingHistory" :xs="24" :sm="24" :md="24" :lg="11" :xl="11">
        <div class="ping-source-notice">
          <svgicon name="icon-info" width="14" height="14" />
          <span>{{ pingSourceNotice }}</span>
        </div>
        <Table
          :data="pingHistoryData"
          :stripe="true"
          :max-height="windowHeight"
          class="table-striped bordered">
          <table-column label="Ping Received" width="220">
            <template slot-scope="scope">
              <span>{{ scope.row.date }}</span>
              <Tooltip
                v-if="scope.row.trackingType && scope.row.trackingType !== trackingType"
                :content="getPingTrackingLabel(scope.row.trackingType)"
                placement="right">
                <span class="ping-type">
                  <svgicon :name="getPingTrackingIcon(scope.row.trackingType)" width="12" height="12" />
                </span>
              </Tooltip>
            </template>
          </table-column>
          <table-column prop="location" label="Location" />
        </Table>
      </Col>
    </Row>
  </div>
</template>

<script>
import { endTripMessage, trackType } from '@/constants/trackConstants';
import { formatDetailedDateTime, formatTime as formatTimeUtil } from '@/lib/Date';
import moment from 'moment';
import Timelineblock from '@common/components/Timelineblock';

export default {
  name: 'TimelineTab',
  components: { Timelineblock },
  props: {
    milestoneEvents: { type: Array, default: () => [] },
    courierTimeline: { type: Array, default: () => [] },
    tripHalts: { type: Array, default: () => [] },
    midwayChanges: { type: Array, default: () => [] },
    endTripDetails: { type: Array, default: () => [] },
    pings: { type: Array, default: () => [] },
    trackingType: { type: String, default: null },
    customerReferenceMap: { type: Object, default: () => ({}) },
    createdAt: { type: String, default: null },
    pickupLocations: { type: Array, default: () => [] },
    dropLocations: { type: Array, default: () => [] },
    entityType: { type: String, default: null },
    timeFormat: { type: String, default: 'hh:mm A' },
    isDelivered: { type: Boolean, default: false },
  },
  computed: {
    hasAnyData() {
      return (this.courierTimeline && this.courierTimeline.length)
        || this.computedMilestones.length
        || this.hasPingHistory;
    },
    hasPingHistory() {
      return this.pings && this.pings.length > 0;
    },
    windowHeight() {
      return `${window.innerHeight - 210}px`;
    },
    pingSourceNotice() {
      const current = trackType.find(t => t.value === this.trackingType);
      const currentName = current ? current.name : 'APP';
      const others = trackType
        .filter(t => t.value && t.value !== (current && current.value))
        .map(t => t.name).join(', ');
      return `Pings are ${currentName}-tracked by default.${others ? ` ${others} icons indicate pings sourced from network or satellite tracking.` : ''}`;
    },
    pingHistoryData() {
      if (!this.pings) return [];
      const sorted = [...this.pings].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return sorted.map(p => ({
        date: formatDetailedDateTime(p.created_at),
        location: p.address || p.city,
        trackingType: p.trackingType || '-',
      }));
    },
    computedMilestones() {
      let events = [];
      events = events.concat(this.getLocationEvents(this.pickupLocations, 'pickup'));
      events = events.concat(this.getLocationEvents(this.dropLocations, 'drop'));
      // End trip details — hide for delivered consignee (belongs to overall trip)
      if (this.endTripDetails && !this.isDelivered) {
        this.endTripDetails.forEach((detail) => {
          const reason = endTripMessage.find(r => r.id === detail.reason);
          if (reason && reason.show) {
            const actor = (this.entityType && this.entityType !== 'NA') ? this.entityType : 'System';
            let orderNo = 'NA';
            if (this.customerReferenceMap && this.customerReferenceMap[detail.customerReference]) {
              orderNo = detail.customerReference;
            }
            events.push({
              timestamp: new Date(detail.timestamp),
              message: 'Trip completed',
              description: reason.message.replace('{actor}', actor).replace('#customerReference#', orderNo),
            });
          }
        });
      }
      // Midway changes — hide for delivered consignee (post-delivery changes not relevant)
      if (this.midwayChanges && !this.isDelivered) {
        [...this.midwayChanges].sort((a, b) => a.timestamp - b.timestamp).forEach((data) => {
          if (!data.delta) return;
          if (this.trackingType === 'SIM') {
            // SIM trips - only show phone changes
            const phoneChanged = data.delta.driverPhoneNumber;
            if (phoneChanged) {
              events.push({
                timestamp: new Date(data.timestamp),
                message: 'Driver Phone Number Changed',
                description: `Driver phone number changed from ${phoneChanged[0]} to ${phoneChanged[1]}`,
              });
            }
          } else {
            // For GPS/APP/other trips - handle vehicle changes
            const vehicleChanged = data.delta.vehicleNumber;
            if (vehicleChanged) {
              events.push({
                timestamp: new Date(data.timestamp),
                message: 'Vehicle Number Changed',
                description: `Vehicle number changed from ${vehicleChanged[0]} to ${vehicleChanged[1]}`,
              });
            }

            // Handle phone changes when secondary SIM tracking exists
            const phoneChanged = data.delta.driverPhoneNumber;
            if (phoneChanged) {
              events.push({
                timestamp: new Date(data.timestamp),
                message: 'Driver Phone Number Changed',
                description: `Driver phone number changed from ${phoneChanged[0]} to ${phoneChanged[1]}`,
              });
            }

            // Show when secondary SIM tracking is added (GPS → SIM fallback)
            const secondaryTrackingChanged = data.delta.secondaryTrackingType;
            if (secondaryTrackingChanged && secondaryTrackingChanged[1] === 'SIM') {
              events.push({
                timestamp: new Date(data.timestamp),
                message: 'Fallback Tracking Enabled',
                description: 'SIM-based tracking activated as secondary tracking',
              });
            }
          }
        });
      }
      // Trip halts — hide for delivered consignee (post-delivery halts not relevant)
      if (this.tripHalts && !this.isDelivered) {
        [...this.tripHalts].sort((a, b) => a.endedAt - b.endedAt).forEach((data) => {
          const duration = moment.duration(moment(data.endedAt).diff(moment(data.startedAt)));
          const totalMins = Number(duration.asMinutes().toFixed(2));
          const hrs = Math.floor(totalMins / 60);
          const mins = Math.floor(totalMins % 60);
          let timeDesc = hrs ? `${hrs} hours` : '';
          const minsDesc = mins ? `${mins} minutes` : '';
          timeDesc = timeDesc ? (minsDesc ? `${timeDesc} and ${minsDesc}` : timeDesc) : minsDesc;
          events.push({
            timestamp: new Date(data.endedAt),
            message: 'Vehicle Halt',
            description: `Vehicle Halted at ${formatTimeUtil(data.startedAt)} for ${timeDesc}`,
          });
        });
      }
      // Trip created
      if (this.createdAt) {
        events.push({ message: 'Trip Created', timestamp: new Date(this.createdAt) });
      }
      events.sort((a, b) => a.timestamp - b.timestamp);
      return events;
    },
  },
  methods: {
    formatDate(date) {
      return date ? this.$moment(date).format('DD MMM YYYY') : null;
    },
    formatTime(date) {
      return date ? this.$moment(date).format(this.timeFormat) : null;
    },
    getPingTrackingIcon(type) {
      return { SIM: 'icon-simcard', GPS: 'icon-transit', APP: 'icon-mobile' }[type] || null;
    },
    getPingTrackingLabel(type) {
      return { SIM: 'SIM Tracking', GPS: 'GPS Tracking', APP: 'App Tracking' }[type] || type;
    },
    getLocationEvents(locations, type) {
      const events = [];
      if (!locations) return events;
      locations.forEach((loc) => {
        if (!loc.timelineData) return;
        const entry = loc.timelineData.find(v => v.movementType === 'entry');
        const exit = loc.timelineData.find(v => v.movementType === 'exit');
        if (entry) {
          events.push({
            orderOfLocation: loc.orderOfLocation,
            timestamp: new Date(entry.time),
            message: type === 'pickup' ? 'At Pickup' : 'Reached Destination',
            description: `Vehicle at ${loc.address.name}`,
            ...(type === 'drop' ? { type: 'destination' } : {}),
          });
        }
        if (exit) {
          events.push({
            orderOfLocation: loc.orderOfLocation,
            timestamp: new Date(exit.time),
            message: type === 'pickup' ? 'Departed from pickup' : 'Released from destination',
            description: `Vehicle left ${loc.address.name}`,
          });
        }
      });
      return events;
    },
  },
};
</script>

<style scoped>
.ping-source-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: #444;
  background-color: #e8f1fb;
  border-radius: 4px;
  padding: 10px 12px;
  line-height: 1.5;
}
.ping-type { margin-left: 10px; }
</style>
